/**
 * Session-scoped holder for the current "surface" (Gated vs Ungated).
 *
 * `baseQuery.ts` is a Redux slice, not a React component, so it cannot read the
 * clientId from `ClientContext` directly. This module provides a tiny, dependency-free
 * bridge: `ClientProvider` publishes the resolved surface via `setApiSurface`, and the
 * API layer (baseQuery / latency logger) reads it via `getApiSurface` to add a `surface`
 * field to log context. That field is extracted as a CloudWatch metric dimension for the
 * SellerExperience-SUW dashboard.
 *
 * Intentionally imports nothing: importing ClientContext here would create a cycle
 * (baseQuery -> apiSurface -> ClientContext -> su -> baseQuery). The Gated/Ungated
 * derivation therefore lives in ClientProvider, which already owns the clientId constants.
 */

export type ApiSurface = 'Gated' | 'Ungated' | 'Widget:Gated' | 'Widget:Ungated';

// Defaults to 'Ungated' to match ClientProvider's default clientId (UNGATED_CLIENT_ID)
// before seller-context resolves. ClientProvider overwrites this on mount.
let currentSurface: ApiSurface = 'Ungated';

/**
 * Publishes the current surface so API-layer log context can be dimensioned by it.
 */
export const setApiSurface = (surface: ApiSurface): void => {
  currentSurface = surface;
};

/**
 * Returns the current surface for inclusion in API log context.
 */
export const getApiSurface = (): ApiSurface => currentSurface;
